// @ts-strict-ignore
import React, { useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { AlignedText } from '@actual-app/components/aligned-text';
import { Button } from '@actual-app/components/button';
import {
  SvgArrowButtonDown1,
  SvgArrowButtonUp1,
} from '@actual-app/components/icons/v2';
import { InitialFocus } from '@actual-app/components/initial-focus';
import { Input } from '@actual-app/components/input';
import { SpaceBetween } from '@actual-app/components/space-between';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { Tooltip } from '@actual-app/components/tooltip';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import type { AccountEntity } from '@actual-app/core/types/models';
import { css, cx } from '@emotion/css';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';

import {
  accountBalanceQueries,
  getPendingBalance,
  useBankDataFreshness,
} from '#account-balances';
import { useReopenAccountMutation, useUpdateAccountMutation } from '#accounts';
import { tryConvertToBaseCurrency } from '#accounts/currency';
import type { RateIndex } from '#accounts/currency';
import { BalanceHistoryGraph } from '#components/accounts/BalanceHistoryGraph';
import { Link } from '#components/common/Link';
import { FinancialText } from '#components/FinancialText';
import { Notes } from '#components/Notes';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { DropHighlight, useDraggable, useDroppable } from '#components/sort';
import type { OnDragChangeCallback, OnDropCallback } from '#components/sort';
import { CellValue } from '#components/spreadsheet/CellValue';
import { useContextMenu } from '#hooks/useContextMenu';
import { useDragRef } from '#hooks/useDragRef';
import { useFormat } from '#hooks/useFormat';
import { useIsTestEnv } from '#hooks/useIsTestEnv';
import { useLocale } from '#hooks/useLocale';
import { useNotes } from '#hooks/useNotes';
import { useSheetValue } from '#hooks/useSheetValue';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { openAccountCloseModal } from '#modals/modalsSlice';
import { useDispatch, useSelector } from '#redux';
import type { Binding, SheetFields } from '#spreadsheet';
import * as bindings from '#spreadsheet/bindings';

export const accountNameStyle: CSSProperties = {
  marginTop: -2,
  marginBottom: 2,
  paddingTop: 4,
  paddingBottom: 4,
  paddingRight: 15,
  paddingLeft: 10,
  textDecoration: 'none',
  color: theme.sidebarItemText,
  ':hover': { backgroundColor: theme.sidebarItemBackgroundHover },
  ...styles.smallText,
};

type AccountProps<FieldName extends SheetFields<'account'>> = {
  name: string;
  to: string;
  // Either a spreadsheet-driven query binding, for the common case of a
  // single account's own balance (single currency, no conversion needed) --
  // or a pre-computed literal `value`, used instead for cross-account group
  // totals in a multi-currency budget, where the total has to be summed
  // client-side after converting each account's balance to the budget's
  // base currency (see sidebar/GroupBalance.tsx) rather than via a single
  // cross-account SQL sum, which can't do that conversion.
  query?: Binding<'account', FieldName>;
  value?: number;
  // Required whenever `account` is a real, single account (not a
  // group-total row) -- built once by the caller (Accounts.tsx) and shared
  // across every row, rather than each row independently re-fetching and
  // re-sorting the same stored rates.
  rateIndex?: RateIndex;
  account?: AccountEntity;
  connected?: boolean;
  pending?: boolean;
  failed?: boolean;
  updated?: boolean;
  style?: CSSProperties;
  outerStyle?: CSSProperties;
  onDragChange?: OnDragChangeCallback<{ id: string }>;
  onDrop?: OnDropCallback;
  titleAccount?: boolean;
  isExactPathMatch?: boolean;
  balanceTestId?: string;
};

export function Account<FieldName extends SheetFields<'account'>>({
  name,
  account,
  connected,
  pending = false,
  failed,
  updated,
  to,
  query,
  value,
  rateIndex,
  style,
  outerStyle,
  onDragChange,
  onDrop,
  titleAccount,
  isExactPathMatch,
  balanceTestId,
}: AccountProps<FieldName>) {
  const isTestEnv = useIsTestEnv();
  const { t } = useTranslation();
  const type = account
    ? account.closed
      ? 'account-closed'
      : account.offbudget
        ? 'account-offbudget'
        : 'account-onbudget'
    : 'title';

  const triggerRef = useRef(null);

  const { dragRef } = useDraggable({
    type,
    onDragChange,
    item: { id: account && account.id },
    canDrag: account != null,
  });
  const handleDragRef = useDragRef(dragRef);

  const { dropRef, dropPos } = useDroppable({
    types: account ? [type] : [],
    id: account && account.id,
    onDrop,
  });

  const [showBalanceHistory, setShowBalanceHistory] = useSyncedPref(
    `side-nav.show-balance-history-${account?.id}`,
  );

  const dispatch = useDispatch();

  const [isEditing, setIsEditing] = useState(false);

  const accountNote = useNotes(`account-${account?.id}`);
  const isTouchDevice =
    window.matchMedia('(hover: none)').matches ||
    window.matchMedia('(pointer: coarse)').matches;
  const needsTooltip = !!account?.id && !isTouchDevice;
  const reopenAccount = useReopenAccountMutation();
  const updateAccount = useUpdateAccountMutation();

  const format = useFormat();
  const baseCurrency = format.currency.code;
  const accountCurrency = account?.currency;
  // Only a real, single-account row (not a group-total row, which uses
  // `value` instead) can be a "foreign" account -- one whose own currency
  // differs from the budget's base currency, needing both a converted
  // (base-currency) and native (own-currency) display.
  // A budget with no base currency set (baseCurrency === '') is not using
  // the currency feature at all -- tryConvertToBaseCurrency returns the
  // amount unchanged in that case, so treating the row as foreign would
  // show the same amount twice ("1,473.12 ($1,473.12)") instead of the
  // plain single-amount display every other account already gets.
  const isForeignCurrencyAccount =
    !!baseCurrency && !!accountCurrency && accountCurrency !== baseCurrency;

  // Hooks below must run unconditionally on every render of a given
  // mounted Account instance (rules of hooks) -- but since a specific
  // instance's `account` prop identity never toggles between "group total"
  // and "real account" during its lifetime, it's safe for the *values*
  // these compute to simply go unused outside the foreign-currency,
  // per-account case.
  const nativeFormat = useFormat(accountCurrency);
  const nativeBalance =
    useSheetValue<'account', 'balance'>(
      bindings.accountBalance(account?.id ?? ''),
    ) ?? 0;

  const convertedBalance =
    isForeignCurrencyAccount && rateIndex
      ? tryConvertToBaseCurrency(
          nativeBalance,
          accountCurrency,
          baseCurrency,
          monthUtils.currentDay(),
          rateIndex,
        )
      : null;

  // Only set when a bank has reported *both* a booked and an available
  // balance for this account (see wailmu/actual#20) -- a bank reporting
  // just one type (or none at all, since nothing populates this table
  // unless a sidecar is polling Enable Banking for it) means nothing extra
  // is shown, rather than a bogus "pending: 0".
  const { data: accountBalances = [] } = useQuery(accountBalanceQueries.list());
  const pendingBalance = account
    ? getPendingBalance(accountBalances, account.id)
    : null;
  // The bank-reported balance carries its own currency (see
  // wailmu/actual#21's review) -- not necessarily the same as
  // `account.currency` (which may be unset, or differ if the account
  // hasn't been tagged with a currency at all) -- so format it separately
  // rather than reusing nativeFormat, which would silently apply the
  // wrong currency symbol in that case.
  const pendingBalanceFormat = useFormat(pendingBalance?.currency);
  // The sidebar only ever shows the "Available" figure, never the
  // pending/difference detail (that's reserved for the account page) --
  // and only when it actually differs from Actual's own computed balance,
  // since showing "Available: same number" again right next to the
  // balance that already displays it would be pure noise. Comparing raw
  // integer amounts alone isn't enough -- a bank-reported balance in a
  // different currency than the ledger can coincidentally share the same
  // raw amount (e.g. an available €100 and a ledger $100 both store as
  // 10000), which would wrongly hide the line precisely when currency is
  // what distinguishes the two values. Only suppress when both the amount
  // *and* currency match.
  const showAvailableInSidebar =
    pendingBalance != null &&
    (pendingBalance.currency !== (accountCurrency ?? baseCurrency) ||
      pendingBalance.available !== nativeBalance);

  // Flag accounts whose bank data has stopped updating (see
  // wailmu/actual#37) -- otherwise a broken bank connection keeps showing
  // its last-known balance as if it were current. Closed accounts are
  // exempt: they stop syncing by design.
  const locale = useLocale();
  const bankDataFreshness = useBankDataFreshness(account?.id);
  const isBankDataStale = !!bankDataFreshness?.isStale && !account?.closed;
  const bankDataUpdatedText = bankDataFreshness
    ? t('Bank data last updated {{when}}', {
        when: formatDistanceToNow(new Date(bankDataFreshness.fetchedAt), {
          addSuffix: true,
          locale,
        }),
      })
    : null;

  const balanceCell = isForeignCurrencyAccount ? (
    <FinancialText style={{ whiteSpace: 'nowrap' }}>
      <PrivacyFilter activationFilters={[true]}>
        {nativeFormat(nativeBalance, 'financial')}
        {
          // No rate on file yet for this pair/date -- show only the native
          // amount rather than a bogus "€0.00" that would misstate the
          // account as worthless (tryConvertToBaseCurrency returns null
          // here, unlike the aggregate-sum convertToBaseCurrency helper,
          // which treats "can't convert" as 0 -- correct for a total, wrong
          // for a single account's own primary displayed value).
          convertedBalance !== null && (
            <>
              {' '}
              <Text
                style={{ ...styles.smallText, color: theme.pageTextSubdued }}
              >
                ({format(convertedBalance, 'financial')})
              </Text>
            </>
          )
        }
      </PrivacyFilter>
    </FinancialText>
  ) : value !== undefined ? (
    <FinancialText style={{ whiteSpace: 'nowrap' }}>
      <PrivacyFilter activationFilters={[true]}>
        {format(value, 'financial')}
      </PrivacyFilter>
    </FinancialText>
  ) : (
    <CellValue binding={query} type="financial" />
  );

  const isContextMenuOpen = useSelector(state =>
    state.contextMenu.items.some(
      i =>
        typeof i === 'object' && 'name' in i && i.name.startsWith('account-'),
    ),
  );
  useContextMenu({
    triggerRef,
    enabled: account && needsTooltip,
    items: [
      {
        name: 'account-rename',
        text: t('Rename'),
        onClick: () => setIsEditing(true),
      },
      account?.closed
        ? {
            name: 'account-reopen',
            text: t('Reopen'),
            onClick: () => reopenAccount.mutate({ id: account.id }),
          }
        : {
            name: 'account-close',
            text: t('Close'),
            onClick: () =>
              dispatch(openAccountCloseModal({ accountId: account.id })),
          },
    ],
  });

  const accountRow = (
    <View innerRef={dropRef} style={{ flexShrink: 0, ...outerStyle }}>
      <View innerRef={triggerRef}>
        <DropHighlight pos={dropPos} />
        <View innerRef={handleDragRef}>
          <Link
            variant="internal"
            to={to}
            isDisabled={isEditing}
            isExactPathMatch={isExactPathMatch}
            style={{
              ...accountNameStyle,
              ...style,
              position: 'relative',
              borderLeft: '4px solid transparent',
              ...(updated && {
                fontWeight: 700,
                color: theme.sidebarItemTextUpdated,
              }),
            }}
            activeStyle={{
              borderColor: theme.sidebarItemAccentSelected,
              color: theme.sidebarItemTextSelected,
              // This is kind of a hack, but we don't ever want the account
              // that the user is looking at to be "bolded" which means it
              // has unread transactions. The system does mark is read and
              // unbolds it, but it still "flashes" bold so this just
              // ignores it if it's active
              fontWeight: (style && style.fontWeight) || 'normal',
              '& .dot': {
                backgroundColor: theme.sidebarItemAccentSelected,
                transform: 'translateX(-4.5px)',
              },
            }}
          >
            <View
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <div
                className={cx(
                  'dot',
                  css({
                    marginRight: 3,
                    width: 5,
                    height: 5,
                    borderRadius: 5,
                    backgroundColor: pending
                      ? theme.sidebarItemBackgroundPending
                      : failed
                        ? theme.sidebarItemBackgroundFailed
                        : theme.sidebarItemBackgroundPositive,
                    marginLeft: 2,
                    transition: 'transform .3s',
                    opacity: connected ? 1 : 0,
                  }),
                )}
              />
            </View>

            <AlignedText
              style={
                titleAccount && {
                  borderBottom: `1.5px solid rgba(255,255,255,0.4)`,
                  paddingBottom: '3px',
                }
              }
              left={
                isEditing ? (
                  <InitialFocus>
                    <Input
                      style={{
                        padding: 0,
                        width: '100%',
                      }}
                      onBlur={() => setIsEditing(false)}
                      onEnter={newAccountName => {
                        if (newAccountName.trim() !== '') {
                          updateAccount.mutate({
                            account: {
                              ...account,
                              name: newAccountName,
                            },
                          });
                        }
                        setIsEditing(false);
                      }}
                      onEscape={() => setIsEditing(false)}
                      defaultValue={name}
                    />
                  </InitialFocus>
                ) : isBankDataStale ? (
                  <Text
                    style={{ color: theme.errorText }}
                    title={bankDataUpdatedText ?? undefined}
                  >
                    {name}
                  </Text>
                ) : (
                  name
                )
              }
              right={
                <View
                  data-testid={balanceTestId}
                  style={{ alignItems: 'flex-end' }}
                >
                  {balanceCell}
                  {showAvailableInSidebar && pendingBalance && (
                    <FinancialText
                      style={{
                        ...styles.smallText,
                        color: theme.pageTextSubdued,
                      }}
                    >
                      <PrivacyFilter activationFilters={[true]}>
                        {t('Available')}{' '}
                        {pendingBalanceFormat(
                          pendingBalance.available,
                          'financial',
                        )}
                      </PrivacyFilter>
                    </FinancialText>
                  )}
                </View>
              }
            />
          </Link>
        </View>
      </View>
    </View>
  );

  if (!needsTooltip || isTestEnv) {
    return accountRow;
  }

  return (
    <Tooltip
      content={
        <View
          style={{
            padding: 10,
          }}
        >
          <SpaceBetween
            gap={5}
            style={{
              justifyContent: 'space-between',
              '& .hover-visible': {
                opacity: 0,
                transition: 'opacity .25s',
              },
              '&:hover .hover-visible': {
                opacity: 1,
              },
            }}
          >
            <Text
              style={{
                fontWeight: 'bold',
              }}
            >
              {name}
            </Text>
            <Button
              aria-label={t('Toggle balance history')}
              variant="bare"
              onClick={() =>
                setShowBalanceHistory(
                  showBalanceHistory === 'true' ? 'false' : 'true',
                )
              }
              className="hover-visible"
            >
              <SpaceBetween gap={3}>
                {showBalanceHistory === 'true' ? (
                  <SvgArrowButtonUp1 width={10} height={10} />
                ) : (
                  <SvgArrowButtonDown1 width={10} height={10} />
                )}
              </SpaceBetween>
            </Button>
          </SpaceBetween>
          {bankDataUpdatedText && (
            <Text
              style={{
                ...styles.smallText,
                color: isBankDataStale
                  ? theme.errorText
                  : theme.pageTextSubdued,
              }}
            >
              {bankDataUpdatedText}
            </Text>
          )}
          {showBalanceHistory === 'true' && account && (
            <BalanceHistoryGraph
              accountId={account.id}
              style={{ minWidth: 350, minHeight: 70 }}
            />
          )}
          {accountNote && (
            <Notes
              getStyle={() => ({
                borderTop: `1px solid ${theme.tableBorder}`,
                padding: 0,
                paddingTop: '0.5rem',
                marginTop: '0.5rem',
              })}
              notes={accountNote}
            />
          )}
        </View>
      }
      style={{ ...styles.tooltip, borderRadius: '0px 5px 5px 0px' }}
      placement="right top"
      triggerProps={{
        delay: 1000,
        closeDelay: 250,
        isDisabled: isContextMenuOpen,
      }}
    >
      {accountRow}
    </Tooltip>
  );
}
